import React from 'react';
import { Form, Select, Space, Typography, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import type { Resource } from '@/api/mcp-appointments';
import { getResourceDisplayName } from '@/utils/serviceResourceLinks';

const { Text } = Typography;
const { Option } = Select;

interface ResourceSelectorProps {
  resources: Resource[] | undefined;
  loading: boolean;
}

export const ResourceSelector: React.FC<ResourceSelectorProps> = ({ resources, loading }) => {
  const { token } = theme.useToken();
  const { t } = useTranslation('manage');

  const resourceOptions = React.useMemo(() => {
    if (!resources) return [];
    return resources.map((resource) => ({
      value: resource.id,
      label: getResourceDisplayName(resource, t('appointmentComponents.resourceFallback', { id: resource.id.slice(0, 8) })),
      type: resource.type
    }));
  }, [resources, t]);

  return (
    <Form.Item
      label={t('appointmentComponents.create.resource.label')}
      name="resourceId"
      rules={[{ required: true, message: t('appointmentComponents.create.resource.required') }]}
    >
      <Select
        placeholder={t('appointmentComponents.create.resource.placeholder')}
        size="middle"
        loading={loading}
        showSearch
        optionFilterProp="label"
        filterOption={(input, option) => {
          const label = option?.label;
          if (typeof label === 'string') {
            return label.toLowerCase().includes(input.toLowerCase());
          }
          const value = String(option?.value ?? '');
          return value.toLowerCase().includes(input.toLowerCase());
        }}
      >
        {resourceOptions.map((option) => (
          <Option key={option.value} value={option.value}>
            <Space>
              <span>{option.label}</span>
              <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                ({option.type})
              </Text>
            </Space>
          </Option>
        ))}
      </Select>
    </Form.Item>
  );
};
